import { RefreshIndicator } from "@components/shared/RefreshIndicator";
import { UNARCHIVE_CAMPAIGN } from "@lib/graphql/mutations";
import { CAMPAIGN_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { useScrollToHash } from "@lib/hooks/useScrollToHash";
import { Archive, ArchiveRestore } from "lucide-react";
import { useCallback } from "react";
import { Outlet, useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { CampaignHeader } from "./detail/CampaignHeader";
import { CampaignTabs } from "./detail/CampaignTabs";
import type { CampaignDetailContext } from "./detail/context";
import { DecisionSummaryPanel } from "./detail/DecisionSummaryPanel";

export function CampaignDetail() {
  const { id } = useParams();
  const [{ data, fetching }, reexecute] = useQuery({ query: CAMPAIGN_QUERY, variables: { id } });
  const [, unarchiveCampaign] = useMutation(UNARCHIVE_CAMPAIGN);
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(id!, refresh);
  useScrollToHash();
  const campaign = data?.campaign;

  if (fetching && !campaign) return <p className="text-sm text-text-muted">Loading...</p>;
  if (!campaign) return <p className="text-sm text-text-muted">Campaign not found</p>;

  async function handleUnarchive() {
    const result = await unarchiveCampaign({ input: { campaignId: campaign!.id } });
    if (result.data?.unarchiveCampaign?.errors?.length === 0) refresh();
  }

  const context: CampaignDetailContext = { campaign, refresh };

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <CampaignHeader campaign={campaign} refresh={refresh} />
        </div>
        <RefreshIndicator active={fetching} />
      </div>
      {campaign.archivedAt && (
        <div className="mt-3 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/30">
          <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
            <Archive size={16} />
            <span>
              This campaign is archived.
              {campaign.archiveReason && <> Reason: {campaign.archiveReason}</>}
            </span>
          </div>
          <button
            type="button"
            onClick={handleUnarchive}
            className="flex items-center gap-1 rounded border border-amber-300 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-800/50"
          >
            <ArchiveRestore size={14} /> Unarchive
          </button>
        </div>
      )}
      <CampaignTabs />
      <div className="mt-6">
        <DecisionSummaryPanel
          campaignId={campaign.id}
          summary={campaign.decisionSummary}
          candidateCount={campaign.candidateCount}
          scoredCandidateCount={campaign.scoredCandidateCount}
        />
        <Outlet context={context} />
      </div>
    </div>
  );
}
